export function reorderList<T = unknown>(list: T[], startIndex: number, endIndex: number) {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
}

export function sortByObjectKey<T>(data: T[], sortByKey: keyof T, sortDesc = false): T[] {
  return data.slice().sort((a, b) => {
    let aVal = a[sortByKey];
    let bVal = b[sortByKey];
    let aValue = typeof aVal === 'string' ? aVal.toLowerCase() : aVal;
    let bValue = typeof bVal === 'string' ? bVal.toLowerCase() : bVal;

    if (bValue < aValue) {
      return !sortDesc ? 1 : -1;
    }
    if (bValue > aValue) {
      return !sortDesc ? -1 : 1;
    }
    return 0;
  });
}

export const arrayMove = <T>(array: Array<T>, fromIndex: number, toIndex: number) => {
  const newArray = array.slice();
  newArray.splice(toIndex, 0, newArray.splice(fromIndex, 1)[0]);
  return newArray;
};

export const getRandomItem = <T>(array: Array<T>): T => {
  return array[Math.floor(Math.random() * array.length)];
};

export const getDistinctPropertyValues = <T extends object>(array: Array<T>, property: keyof T) => {
  return [...new Set(array.map((item) => item[property]))];
};

export const arrayToTrueMap = (array: Array<string>) => {
  return array.reduce((acc, value) => {
    acc[value] = true;
    return acc;
  }, {} as Record<string, true>);
};

export const countTruthy = (...items: Array<any>) => {
  return items.filter(Boolean).length;
};

export const sanitizeJSON = <T>(data: T, maxStringLength = 1000): T => {
  if (typeof data === 'string') {
    // Truncate strings longer than the max length
    return (data.length > maxStringLength
      ? data.slice(0, maxStringLength) + '... (truncated)'
      : data) as unknown as T;
  } else if (Array.isArray(data)) {
    // Recursively sanitize each item in the array
    return data.map((item) => sanitizeJSON(item, maxStringLength)) as unknown as T;
  } else if (typeof data === 'object' && data !== null) {
    // Recursively sanitize each key in the object
    const sanitizedObject: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data)) {
      sanitizedObject[key] = sanitizeJSON(value, maxStringLength);
    }
    return sanitizedObject as T;
  }
  // Return other types (numbers, booleans, null, etc.) as is
  return data;
};

// Meant to mimic the classNames npm package
// https://www.npmjs.com/package/classnames
export const classNames = (...args: ClassValue[]): string => {
  let classes = '';
  for (const arg of args) {
    if (arg) {
      classes = appendClass(classes, parseValue(arg));
    }
  }
  return classes;
};

export const removeObjectKey = <T extends object>(object: T, keyToRemove: keyof T) => {
  return Object.fromEntries(Object.entries(object).filter(([key]) => key !== keyToRemove));
};

type ClassValue = string | number | null | undefined | boolean | ClassDictionary | ClassArray;

type ClassDictionary = {
  [key: string]: any;
};

type ClassArray = Array<ClassValue>;

const appendClass = (value: string, newClass: string): string =>
  newClass ? (value ? `${value} ${newClass}` : newClass) : value;

const parseValue = (arg: ClassValue): string => {
  if (typeof arg === 'string' || typeof arg === 'number') {
    return String(arg);
  }

  if (typeof arg !== 'object' || arg === null) {
    return '';
  }

  if (Array.isArray(arg)) {
    return classNames(...arg);
  }

  // Handle objects with string keys and boolean values
  let classes = '';
  for (const key in arg) {
    if (Object.prototype.hasOwnProperty.call(arg, key) && arg[key]) {
      classes = appendClass(classes, key);
    }
  }

  return classes;
};
